import { useState } from 'react';
import { Ambulance, CheckCircle2, Phone } from 'lucide-react';
import { patientApi } from '../api';
import type { MarketplaceProvider } from '../types';
import BookingModal from './BookingModal';

interface Props {
  provider: MarketplaceProvider;
  open: boolean;
  onClose: () => void;
}

export default function BookAmbulanceForm({ provider, open, onClose }: Props) {
  const [pickupAddress, setPickupAddress] = useState('');
  const [emergencyType, setEmergencyType] = useState('GENERAL');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await patientApi.bookAmbulance({
        providerId: provider.id,
        pickupAddress: pickupAddress || undefined,
        emergencyType,
        notes: notes || undefined,
      });
      setSuccess(result);
    } catch {
      alert('Request failed. Please try again or call the ambulance service directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <BookingModal title="Ambulance Requested" open={open} onClose={onClose}>
        <div className="text-center py-4 space-y-3">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h3 className="text-lg font-bold text-gray-900">Ambulance Requested!</h3>
          <p className="text-sm text-gray-600">{success.message}</p>
          <div className="bg-orange-50 rounded-xl p-3 space-y-1">
            <p className="text-xs text-orange-600 uppercase tracking-wider font-semibold">Estimated Arrival</p>
            <p className="text-xl font-bold text-orange-700">{success.estimatedArrival}</p>
          </div>
          {success.provider?.phone && (
            <a
              href={`tel:${success.provider.phone}`}
              className="flex items-center justify-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700"
            >
              <Phone className="w-4 h-4" /> Call {success.provider.phone}
            </a>
          )}
          <button onClick={onClose} className="btn-primary w-full mt-4">Done</button>
        </div>
      </BookingModal>
    );
  }

  return (
    <BookingModal title="Request Ambulance" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl">
          <Ambulance className="w-5 h-5 text-orange-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900">{provider.name}</p>
            <p className="text-xs text-gray-500">{provider.phone}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Type</label>
          <select className="input" value={emergencyType} onChange={e => setEmergencyType(e.target.value)}>
            <option value="GENERAL">General Emergency</option>
            <option value="CARDIAC">Cardiac Emergency</option>
            <option value="ACCIDENT">Accident / Trauma</option>
            <option value="MATERNITY">Maternity</option>
            <option value="TRANSFER">Hospital Transfer</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address</label>
          <textarea
            className="input"
            rows={2}
            placeholder="Full pickup address or landmark..."
            value={pickupAddress}
            onChange={e => setPickupAddress(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
          <textarea
            className="input"
            rows={2}
            placeholder="Patient condition, floor number, gate details..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        <p className="text-[10px] text-gray-400 leading-relaxed">By confirming, you consent to sharing your location and medical details with the ambulance provider. In a real emergency, always call 112/108 directly.</p>

        <button type="submit" disabled={submitting} className="btn-primary w-full bg-orange-600 hover:bg-orange-700">
          {submitting ? 'Requesting...' : 'Request Ambulance Now'}
        </button>
      </form>
    </BookingModal>
  );
}
